import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";

import { DataTableSkeleton, PageHeaderSkeleton } from "components/admin";
import { AppLayout } from "components/layouts/app-layout/app-layout";
import { useAuth } from "context/auth-context";
import { useWorkspaceProfile } from "./hooks/use-workspace-profile";
import { PlayerPerformanceSectionSkeleton } from "./sections/player-performance-section.skeleton";
import { WorkspaceSectionContent } from "./workspace-content";
import { WorkspaceContentStack } from "pages/app/workspace/styles/index.inline.styles";
import { PortalSection } from "utils/portal-access";

const WorkspaceLoadingState: React.FC<{
  isAthletePortal?: boolean;
  section: PortalSection | null;
}> = ({ isAthletePortal = false, section }) => {
  if (section === "player_performance_records") {
    return (
      <WorkspaceContentStack>
        <PlayerPerformanceSectionSkeleton
          isAthleteAccount={isAthletePortal}
          showHighlights={!isAthletePortal}
        />
      </WorkspaceContentStack>
    );
  }

  return (
    <WorkspaceContentStack>
      <PageHeaderSkeleton />
      <DataTableSkeleton columns={4} rows={6} />
    </WorkspaceContentStack>
  );
};

const getWorkspaceSectionSlug = (pathname: string) => {
  const match = pathname.match(/^\/workspace\/([^/]+)/);
  return match?.[1] || "";
};

const WorkspacePage: React.FC = () => {
  const location = useLocation();
  const sectionSlug = useMemo(
    () => getWorkspaceSectionSlug(location.pathname),
    [location.pathname],
  );
  const { portalAccess } = useAuth();
  const { currentUser, effectivePortalAccess, isLoading, route, section } =
    useWorkspaceProfile(sectionSlug);
  const isAthletePortal = (effectivePortalAccess || portalAccess)?.portal_type === "athlete";

  return (
    <AppLayout route={route}>
      {isLoading ? (
        <WorkspaceLoadingState isAthletePortal={isAthletePortal} section={section} />
      ) : (
        <WorkspaceSectionContent
          key={sectionSlug}
          currentUser={currentUser}
          effectivePortalAccess={effectivePortalAccess}
          section={section}
        />
      )}
    </AppLayout>
  );
};
export default WorkspacePage;
